/**
 * Customer artifacts route (OL-158) — eagerly registers
 * `/orca/artifacts/$issueIdentifier` and re-applies its OWN guard; the page
 * component and its atom/RPC imports are deferred to
 * `artifacts.$issueIdentifier.lazy.tsx`.
 *
 * `orcaloops.ai/artifacts/<identifier>` rewrites onto this path — every
 * orcaloops.ai path does, because `/orca` is a WILDCARD domain prefix. That is
 * why the file lives under `routes/orca/` and not behind a `routes/orca_/`
 * escape, which would register a bare `/artifacts/...` the rewrite can never
 * reach.
 *
 * **`requireAuth`, never `requireAdmin`** — the collect route's pattern
 * exactly. `/orca/artifacts` is an `ORCA_PUBLIC_ROOTS` entry so the `/orca`
 * layout guard skips the admin check; this guard puts a session back in its
 * place. The person reading their own build's transcript is not an operator,
 * and `requireAdmin` would bounce the page's only audience to
 * `/access-denied`.
 *
 * The session is necessary but not sufficient: `orcaloops.archive.*` resolves
 * the owning customer from `CurrentActor` and answers `Forbidden` — with the
 * same shape for an identifier that does not exist and one owned by somebody
 * else — for everybody else.
 *
 * The identifier check runs BEFORE the auth call: a junk segment is not a task
 * on anybody's account, so it is a not-found, and answering it without a round
 * trip keeps the guard from being a login funnel for garbage URLs.
 *
 * v1 is deliberately DEEP-LINK ONLY. Nothing in the app links here: there is no
 * customer run list to hang it off yet, and inventing a customer navigation
 * shell under an archive ticket would smuggle in a second, unreviewed product
 * surface. When the customer-facing outcome surface lands, its run row is where
 * this link belongs.
 *
 * @since 1.0.0
 */
import { createFileRoute, notFound } from "@tanstack/react-router";

import { isLinearIssueIdentifier } from "~/lib/orca-issue-identifier.ts";
import { orcaloopsStylesheetLinks } from "~/lib/orcaloops-head.ts";
import { requireAuth } from "~/lib/server-auth.ts";

/**
 * The guard, extracted so it is unit-testable without the route machinery.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
export const orcaArtifactsBeforeLoad = async (
  args: { readonly params?: Readonly<Record<string, string>> },
) => {
  if (!isLinearIssueIdentifier(args.params?.issueIdentifier)) {
    throw notFound();
  }
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
  const auth = await requireAuth();
  return { auth };
};

/**
 * The route's `head()`: the v5 orcaloops frame's stylesheet, linked here so it
 * is on the page before the lazy chunk's markup paints. Extracted so it is
 * unit-testable without the route machinery.
 *
 * @since 1.0.0
 */
export const artifactsHead = () => ({ links: orcaloopsStylesheetLinks });

export const Route = createFileRoute("/orca/artifacts/$issueIdentifier")({
  beforeLoad: orcaArtifactsBeforeLoad,
  head: artifactsHead,
});
